import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import Zoom from '@mui/material/Zoom';
import Skeleton from '@mui/material/Skeleton';
import axios from 'axios';
import { supabase } from '../utils/supabaseClient';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Button from '@mui/material/Button';
import PaidIcon from '@mui/icons-material/Paid';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InventoryIcon from '@mui/icons-material/Inventory';
import BlockIcon from '@mui/icons-material/Block';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import RefreshIcon from '@mui/icons-material/Refresh';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';

function LeaderboardsView({ selectedProvider, selectedWeek, formatCurrency, formatNumber, formatPercentage }) {
  const [leaderboards, setLeaderboards] = useState({
    topMargin: [],
    topPayments: [],
    topCompletions: [],
    topRefusals: [],
    topRevenue: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // For animation resets

  // Common styles for leaderboard components
  const commonStyles = useMemo(() => ({
    paper: {
      p: 3,
      height: '100%',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    },
    listItem: {
      mb: 1,
      borderRadius: 1,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'scale(1.02)',
        bgcolor: 'action.hover'
      }
    },
    avatar: {
      width: 40,
      height: 40,
      fontSize: '1.2rem',
      fontWeight: 'bold'
    },
    chip: {
      fontWeight: 'bold',
      minWidth: 100,
      justifyContent: 'center'
    }
  }), []);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <Box sx={{ width: '100%', p: 2 }}>
      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
      {[1, 2, 3, 4, 5].map((i) => (
        <Box key={i} sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="60%" height={20} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  // Error component with retry button
  const ErrorDisplay = ({ message, onRetry, color = 'primary' }) => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="body1" color="error" gutterBottom>
        {message}
      </Typography>
      <Tooltip 
        title="Try loading the data again"
        arrow
        placement="top"
      >
        <Button 
          variant="outlined" 
          color={color}
          size="small" 
          onClick={onRetry}
          startIcon={<RefreshIcon />}
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Tooltip>
    </Box>
  );

  // Empty state component
  const EmptyState = ({ message }) => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  useEffect(() => {
    fetchLeaderboardsData();
  }, [selectedProvider, selectedWeek]);

  const fetchLeaderboardsData = async () => {
    try {
      setLoading(true);
      setError(null);
      setRefreshKey(prev => prev + 1); // Reset animations

      // Get auth token
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;
      
      if (!token) {
        setError('No authentication token available');
        return;
      }

      // Prepare URL with optional filters
      const params = new URLSearchParams();
      
      if (selectedProvider) {
        params.append('provider', selectedProvider);
      }
      
      if (selectedWeek) {
        const weekStartDate = startOfWeek(selectedWeek, { weekStartsOn: 1 });
        const weekEndDate = endOfWeek(selectedWeek, { weekStartsOn: 1 });
        
        params.append('work_week_from_date', weekStartDate.toISOString());
        params.append('work_week_to_date', weekEndDate.toISOString());
      }
      
      let url = '/api/dashboard/leaderboards';
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      // Make API request
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.success) {
        setLeaderboards(response.data.data || {
          topMargin: [],
          topPayments: [],
          topCompletions: [],
          topRefusals: [],
          topRevenue: []
        });
      } else {
        setError(response.data?.message || 'Failed to fetch leaderboard data');
      }
    } catch (error) {
      console.error('Error fetching leaderboards data:', error);
      setError('An error occurred while fetching leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  // Render the Top Margin leaderboard
  const MarginLeaderboard = () => (
    <Paper sx={commonStyles.paper}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LeaderboardIcon color="primary" />
        <Typography variant="h6" sx={{ ml: 1 }}>Top Providers by Margin</Typography>
        <Tooltip 
          title={
            <React.Fragment>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Profit Margin Analysis</Typography>
              <Typography variant="body2">
                Shows providers with the highest profit margins, calculated as profit divided by revenue.
                Higher margins indicate more efficient operations and better profitability.
              </Typography>
            </React.Fragment>
          } 
          arrow 
          placement="top"
          sx={{ ml: 1 }}
        >
          <InfoIcon color="action" fontSize="small" sx={{ ml: 1, cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorDisplay message={error} onRetry={fetchLeaderboardsData} />
      ) : leaderboards.topMargin.length > 0 ? (
        <List sx={{ width: '100%' }}>
          {leaderboards.topMargin.map((item, index) => (
            <Fade in={true} style={{ transitionDelay: `${index * 100}ms` }} key={item.provider}>
              <ListItem
                alignItems="flex-start"
                sx={commonStyles.listItem}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: index === 0 ? 'primary.main' : 
                              index === 1 ? 'primary.dark' : 
                              index === 2 ? 'info.main' : 'grey.500'
                    }}
                  >
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" component="span" fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {item.provider}
                      </Typography>
                      <Tooltip 
                        title="Profit margin percentage (profit ÷ revenue)"
                        arrow
                        placement="top"
                      >
                        <Chip 
                          label={formatPercentage(item.margin)} 
                          color={index === 0 ? 'primary' : 'default'}
                          size="small"
                        />
                      </Tooltip>
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <MonetizationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Total revenue generated from all locations"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Revenue: {formatCurrency(item.revenue)}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Net profit after contractor payments"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Profit: {formatCurrency(item.profit)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </React.Fragment>
                  }
                />
              </ListItem>
            </Fade>
          ))}
        </List>
      ) : (
        <EmptyState message="No margin data available." />
      )}
    </Paper>
  );

  // Render the Top Payments leaderboard
  const PaymentsLeaderboard = () => (
    <Paper sx={commonStyles.paper}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PaidIcon color="secondary" />
        <Typography variant="h6" sx={{ ml: 1 }}>Top Providers by Payments</Typography>
        <Tooltip 
          title={
            <React.Fragment>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Contractor Payments Analysis</Typography>
              <Typography variant="body2">
                Highlights providers receiving the highest payments for their services.
                This metric reflects contractor compensation and correlates with work volume and quality.
              </Typography>
            </React.Fragment>
          } 
          arrow 
          placement="top"
        >
          <InfoIcon color="action" fontSize="small" sx={{ ml: 1, cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorDisplay message={error} onRetry={fetchLeaderboardsData} />
      ) : leaderboards.topPayments.length > 0 ? (
        <List sx={{ width: '100%' }}>
          {leaderboards.topPayments.map((item, index) => (
            <Fade in={true} style={{ transitionDelay: `${index * 100}ms` }} key={item.provider}>
              <ListItem
                alignItems="flex-start"
                sx={commonStyles.listItem}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: index === 0 ? 'secondary.main' : 
                              index === 1 ? 'secondary.dark' : 
                              index === 2 ? 'info.main' : 'grey.500'
                    }}
                  >
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" component="span" fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {item.provider}
                      </Typography>
                      <Tooltip 
                        title="Total payments made to this provider"
                        arrow
                        placement="top"
                      >
                        <Chip 
                          label={formatCurrency(item.totalPayments)} 
                          color={index === 0 ? 'secondary' : 'default'}
                          size="small"
                        />
                      </Tooltip>
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <WorkIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Number of completed service locations"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Completions: {formatNumber(item.completions)}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <MonetizationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Total revenue from all completed locations"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Revenue Generated: {formatCurrency(item.revenue)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </React.Fragment>
                  }
                />
              </ListItem>
            </Fade>
          ))}
        </List>
      ) : (
        <EmptyState message="No payment data available." />
      )}
    </Paper>
  );

  // Render the Top Completions leaderboard
  const CompletionsLeaderboard = () => (
    <Paper sx={commonStyles.paper}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <EmojiEventsIcon color="success" />
        <Typography variant="h6" sx={{ ml: 1 }}>Top Providers by Completions</Typography>
        <Tooltip 
          title={
            <React.Fragment>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Location Completions</Typography>
              <Typography variant="body2">
                Shows providers who have completed the most locations.
                High completion counts indicate consistent performance and service reliability.
                These providers contribute significantly to overall revenue generation.
              </Typography>
            </React.Fragment>
          } 
          arrow 
          placement="top"
        >
          <InfoIcon color="action" fontSize="small" sx={{ ml: 1, cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorDisplay message={error} onRetry={fetchLeaderboardsData} />
      ) : leaderboards.topCompletions.length > 0 ? (
        <List sx={{ width: '100%' }}>
          {leaderboards.topCompletions.map((item, index) => (
            <Fade in={true} style={{ transitionDelay: `${index * 100}ms` }} key={item.provider}>
              <ListItem
                alignItems="flex-start"
                sx={commonStyles.listItem}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: index === 0 ? 'success.main' : 
                              index === 1 ? 'success.dark' : 
                              index === 2 ? 'info.main' : 'grey.500'
                    }}
                  >
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" component="span" fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {item.provider}
                      </Typography>
                      <Tooltip 
                        title="Total number of service locations completed"
                        arrow
                        placement="top"
                      >
                        <Chip 
                          label={`${formatNumber(item.completions)} locations`}
                          color={index === 0 ? 'success' : 'default'}
                          size="small"
                        />
                      </Tooltip>
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Total revenue from completed services"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Revenue: {formatCurrency(item.revenue)}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <InventoryIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Total number of units serviced"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Total Units: {formatNumber(item.totalUnits || 0)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </React.Fragment>
                  }
                />
              </ListItem>
            </Fade>
          ))}
        </List>
      ) : (
        <EmptyState message="No completion data available." />
      )}
    </Paper>
  );

  // Render the Top Refusals leaderboard
  const RefusalsLeaderboard = () => (
    <Paper sx={commonStyles.paper}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <BlockIcon color="error" />
        <Typography variant="h6" sx={{ ml: 1 }}>Top Providers by Refusals</Typography>
        <Tooltip 
          title={
            <React.Fragment>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Service Refusal Analysis</Typography>
              <Typography variant="body2">
                Identifies providers with the highest number of refused units.
                Refusals may indicate service challenges, customer issues, or operational inefficiencies.
                Monitoring this metric helps improve overall service quality.
              </Typography>
            </React.Fragment>
          } 
          arrow 
          placement="top"
        >
          <InfoIcon color="action" fontSize="small" sx={{ ml: 1, cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorDisplay message={error} onRetry={fetchLeaderboardsData} />
      ) : leaderboards.topRefusals.length > 0 ? (
        <List sx={{ width: '100%' }}>
          {leaderboards.topRefusals.map((item, index) => (
            <Fade in={true} style={{ transitionDelay: `${index * 100}ms` }} key={item.provider}>
              <ListItem
                alignItems="flex-start"
                sx={commonStyles.listItem}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: index === 0 ? 'error.main' : 
                              index === 1 ? 'error.dark' : 
                              index === 2 ? 'info.main' : 'grey.500'
                    }}
                  >
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" component="span" fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {item.provider}
                      </Typography>
                      <Tooltip 
                        title="Total number of units where service was refused"
                        arrow
                        placement="top"
                      >
                        <Chip 
                          label={`${formatNumber(item.refusals)} units`}
                          color={index === 0 ? 'error' : 'default'}
                          size="small"
                        />
                      </Tooltip>
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <DoNotDisturbIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Revenue lost from refused services"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Revenue: {formatCurrency(item.revenue)}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <MonetizationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Total payments made despite refusals"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Payment: {formatCurrency(item.totalPayments)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </React.Fragment>
                  }
                />
              </ListItem>
            </Fade>
          ))}
        </List>
      ) : (
        <EmptyState message="No refusal data available." />
      )}
    </Paper>
  );

  // Render the Top Revenue leaderboard
  const RevenueLeaderboard = () => (
    <Paper sx={commonStyles.paper}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LocalDrinkIcon color="warning" />
        <Typography variant="h6" sx={{ ml: 1 }}>Top Providers by Coca Cola Revenue</Typography>
        <Tooltip 
          title={
            <React.Fragment>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Coca Cola Revenue Generation</Typography>
              <Typography variant="body2">
                Shows providers generating the highest revenue from Coca Cola products.
                This metric combines both volume and value of sales, highlighting top performers.
                Details include profit margins, completion rates, and refusal counts for context.
              </Typography>
            </React.Fragment>
          } 
          arrow 
          placement="top"
        >
          <InfoIcon color="action" fontSize="small" sx={{ ml: 1, cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorDisplay message={error} onRetry={fetchLeaderboardsData} />
      ) : leaderboards.topRevenue.length > 0 ? (
        <List sx={{ width: '100%' }}>
          {leaderboards.topRevenue.map((item, index) => (
            <Fade in={true} style={{ transitionDelay: `${index * 100}ms` }} key={item.provider}>
              <ListItem
                alignItems="flex-start"
                sx={commonStyles.listItem}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: index === 0 ? 'warning.main' : 
                              index === 1 ? 'warning.dark' : 
                              index === 2 ? 'info.main' : 'grey.500'
                    }}
                  >
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" component="span" fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {item.provider}
                      </Typography>
                      <Tooltip 
                        title="Total revenue generated from Coca Cola products"
                        arrow
                        placement="top"
                      >
                        <Chip 
                          label={formatCurrency(item.revenue)}
                          color={index === 0 ? 'warning' : 'default'}
                          size="small"
                        />
                      </Tooltip>
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Net profit after contractor payments"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Profit: {formatCurrency(item.profit)}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <WorkIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Number of locations with completed service"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Completions: {formatNumber(item.completions)} locations
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <BlockIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Tooltip 
                          title="Units where service was refused"
                          arrow
                          placement="right"
                        >
                          <Typography component="span" variant="body2" color="text.primary">
                            Refusals: {formatNumber(item.refusals)} units
                          </Typography>
                        </Tooltip>
                      </Box>
                    </React.Fragment>
                  }
                />
              </ListItem>
            </Fade>
          ))}
        </List>
      ) : (
        <EmptyState message="No revenue data available." />
      )}
    </Paper>
  );

  return (
    <Fade in={true} timeout={600}>
      <Box>
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 3
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Provider Leaderboards
                <Tooltip 
                  title={
                    <React.Fragment>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>About Provider Leaderboards</Typography>
                      <Typography variant="body2">
                        This dashboard presents performance rankings across five key metrics:
                        <ul style={{ paddingLeft: '16px', marginTop: '4px', marginBottom: '4px' }}>
                          <li>Profit Margin - Shows providers with highest profitability</li>
                          <li>Payments - Highlights highest paid contractors</li>
                          <li>Completions - Ranks by number of serviced locations</li>
                          <li>Refusals - Shows instances where service was declined</li>
                          <li>Revenue - Identifies top revenue generators</li>
                        </ul>
                        Use these insights to identify top performers and improvement opportunities.
                      </Typography>
                    </React.Fragment>
                  } 
                  arrow 
                  placement="right"
                  sx={{ ml: 1 }}
                >
                  <InfoIcon color="primary" fontSize="small" sx={{ ml: 1, cursor: 'pointer', verticalAlign: 'middle' }} />
                </Tooltip>
                {selectedProvider && (
                  <Typography component="span" variant="subtitle1" sx={{ ml: 2 }}>
                    (Filtered by: {selectedProvider})
                  </Typography>
                )}
                {selectedWeek && (
                  <Typography component="span" variant="subtitle1" sx={{ ml: selectedProvider ? 0 : 2 }}>
                    {selectedProvider ? ' for ' : '(Filtered by: '} 
                    Week of {format(startOfWeek(selectedWeek, { weekStartsOn: 1 }), 'MMM d, yyyy')}
                    {!selectedProvider && ')'}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Performance rankings of providers across different metrics
              </Typography>
            </Box>
            <Tooltip 
              title="Refresh leaderboard data with latest information"
              arrow
              placement="left"
            >
              <span>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={fetchLeaderboardsData}
                  disabled={loading}
                  sx={{ minWidth: 100 }}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                <Box>
                  <MarginLeaderboard />
                </Box>
              </Zoom>
            </Grid>
            <Grid item xs={12} md={6}>
              <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                <Box>
                  <PaymentsLeaderboard />
                </Box>
              </Zoom>
            </Grid>
            <Grid item xs={12} md={6}>
              <Zoom in={true} style={{ transitionDelay: '300ms' }}>
                <Box>
                  <CompletionsLeaderboard />
                </Box>
              </Zoom>
            </Grid>
            <Grid item xs={12} md={6}>
              <Zoom in={true} style={{ transitionDelay: '400ms' }}>
                <Box>
                  <RefusalsLeaderboard />
                </Box>
              </Zoom>
            </Grid>
            <Grid item xs={12}>
              <Zoom in={true} style={{ transitionDelay: '500ms' }}>
                <Box>
                  <RevenueLeaderboard />
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Fade>
  );
}

export default LeaderboardsView; 